
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Settings, Shield, Database, Bell, Users, Lock } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { useSupabaseAuth } from '@/hooks/auth';
import { useRealTimeSystemMetrics } from '@/hooks/useRealTimeSystemMetrics';
import RealTimeMetricCards from '@/components/admin/RealTimeMetricCards';
import { logger } from '@/utils/logger';

const SystemSettings = () => {
  const navigate = useNavigate();
  const { hasAccess } = useSupabaseAuth();
  const { 
    metrics, 
    loading, 
    isConnected, 
    lastUpdate, 
    refreshMetrics, 
    simulateUpdate 
  } = useRealTimeSystemMetrics();

  React.useEffect(() => {
    logger.info('SystemSettings page loaded', { isConnected, loading });
  }, [isConnected, loading]);

  if (!hasAccess('admin')) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-muted-foreground">You don't have admin permissions to access system settings.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Loading System Settings...</h1>
          <p className="text-muted-foreground">Connecting to real-time metrics...</p>
        </div>
      </div>
    );
  }

  const settingsCategories = [
    {
      title: "Security & Authentication",
      description: "Manage authentication settings, password policies, and security configurations",
      icon: Shield,
      route: "/admin/settings/security",
      items: ["Password Policy", "Two-Factor Authentication", "Session Management", "API Keys"]
    },
    {
      title: "Database Configuration",
      description: "Database connections, backup settings, and data retention policies",
      icon: Database,
      route: "/admin/settings/database",
      items: ["Connection Settings", "Backup Schedule", "Data Retention", "Performance Tuning"]
    },
    {
      title: "Notification Settings",
      description: "Email templates, notification preferences, and system alerts",
      icon: Bell,
      route: "/admin/settings/notifications",
      items: ["Email Templates", "SMS Settings", "Push Notifications", "Alert Thresholds"]
    },
    {
      title: "User Management",
      description: "Default user roles, registration settings, and access controls",
      icon: Users,
      route: "/admin/settings/users",
      items: ["Default Roles", "Registration Settings", "Access Controls", "User Limits"]
    },
    {
      title: "System Configuration",
      description: "General system settings, feature flags, and maintenance mode",
      icon: Settings,
      route: "/admin/settings/system",
      items: ["Feature Flags", "Maintenance Mode", "System Limits", "Cache Settings"]
    },
    {
      title: "Privacy & Compliance",
      description: "GDPR settings, data privacy controls, and compliance configurations",
      icon: Lock,
      route: "/admin/settings/privacy",
      items: ["GDPR Settings", "Data Privacy", "Audit Logs", "Compliance Reports"]
    }
  ];

  const handleCategoryClick = (route: string) => {
    navigate(route);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">System Settings</h1>
          <p className="text-muted-foreground">Configure system-wide settings and monitor real-time performance</p>
        </div>
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          Admin Access
        </Badge>
      </div>

      {/* Real-Time Metrics Section */}
      <RealTimeMetricCards
        metrics={metrics}
        isConnected={isConnected}
        lastUpdate={lastUpdate}
        onRefresh={refreshMetrics}
        onSimulateUpdate={simulateUpdate}
      />

      {/* Settings Categories */}
      <div>
        <h2 className="text-2xl font-bold mb-6">Configuration Categories</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {settingsCategories.map((category) => {
            const IconComponent = category.icon;
            
            return (
              <Card key={category.title} className="hover:shadow-lg transition-shadow cursor-pointer">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <IconComponent className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{category.title}</CardTitle>
                    </div>
                  </div>
                  <CardDescription className="text-sm">
                    {category.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    {category.items.map((item, index) => (
                      <div key={index} className="text-sm text-muted-foreground flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full"></div>
                        {item}
                      </div>
                    ))}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={() => handleCategoryClick(category.route)}
                  >
                    Configure
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;
